import { AfterViewInit, Component, OnInit } from '@angular/core';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import { DatasourceColumnId } from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { createCase10 } from './data-loaders/case_10_quarterly';
import { createCase11 } from './data-loaders/case_11_break_line_on_missing_data';
import { createCase11b } from './data-loaders/case_11b_break_line_on_missing_data';
import { createCase12 } from './data-loaders/case_12_numeric_category_axis';
import { createCase1 } from './data-loaders/case_1_monthly';
import { createCase2 } from './data-loaders/case_2_monthly';
import { createCase3 } from './data-loaders/case_3_yearly';
import { createCase4 } from './data-loaders/case_4_revenue_daily_last_60d';
import { createCase5 } from './data-loaders/case_5_single_data_point';
import { createCase6 } from './data-loaders/case_6_weekly';
import { createCase7 } from './data-loaders/case_7_commits_cnt';
import { createCase8 } from './data-loaders/case_8_split_and_no_data';
import { createCase9 } from './data-loaders/case_9_monthly_long';

@Component({
    selector: 'app-dev-echarts',
    templateUrl: './dev-echarts.component.html',
    styleUrls: ['./dev-echarts.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class DevEChartsChomponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'ECharts';
    override DASHBOARD_ID = 'dev-echarts';
    values = Object.values;

    /** GCP Project id used in the dashboard */
    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.users.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.users.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'dev.echarts.case_1': createCase1(),
        'dev.echarts.case_2': createCase2(),
        'dev.echarts.case_3': createCase3(),
        'dev.echarts.case_4': createCase4(),
        'dev.echarts.case_5': createCase5(),
        'dev.echarts.case_6': createCase6(),
        'dev.echarts.case_7': createCase7(),
        'dev.echarts.case_8': createCase8(),
        'dev.echarts.case_9_monthly_long': createCase9(),
        'dev.echarts.case_10_quarterly': createCase10(),
        'dev.echarts.case_11_break_line_on_missing_data': createCase11(),
        'dev.echarts.case_11b_break_line_on_missing_data': createCase11b(),
        'dev.echarts.case_12_numeric_category_axis': createCase12(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    // CHARTS
    case_1: IDashboardItemDataLoaderLink = {
        id: 'lnk.dev.echarts.chart.case_1',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'dev.echarts.chart.case_1',
            dashboardItem: { title: 'Monthly new users' },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'New users',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            tooltip: {
                xLabelFormatterGetter: () =>
                    EChartsAxisHelper.axisLabelDateFormatter,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_1'].emitter,
    };

    case_2: IDashboardItemDataLoaderLink = {
        id: 'lnk.dev.echarts.chart.case_2',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'dev.echarts.chart.case_2',
            dashboardItem: { title: 'Monthly new users' },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'New users',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            tooltip: {
                xLabelFormatterGetter: () =>
                    EChartsAxisHelper.axisLabelDateFormatter,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_2'].emitter,
    };
    case_9_monthly_long: IDashboardItemDataLoaderLink = {
        id: 'lnk.dev.echarts.chart.case_9_monthly_long',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'dev.echarts.chart.case_9_monthly_long',
            dashboardItem: {
                title: 'Monthly new users - long time period',
                chartDocumentation:
                    'Previously, there was an issue where the dashboard would crash when a large date range was selected together with monthly aggregation. The root cause was an additional if statement in xy-chart-axis.moveTick() that created infinite loop to find the next tick.',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'New users',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            tooltip: {
                xLabelFormatterGetter: () =>
                    EChartsAxisHelper.axisLabelDateFormatter,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['dev.echarts.case_9_monthly_long'].emitter,
    };
    case_3_revenue_yearly: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.case_3_revenue_yearly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.case_3_revenue_yearly',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                subtitle:
                    'Data is agreggated yearly so custom values should be yearly',
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            yColumnLabel: 'revenue_net_pln',
            metricName: 'Revenue net PLN',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_3'].emitter,
    };

    case_4_revenue_daily: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.case_4_revenue_daily',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.case_4_revenue_daily',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                subtitle:
                    'Daily data. Timestanp is over a year so xAxis labels and tick can be every month',
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            yColumnLabel: 'revenue_net_pln',
            metricName: 'Revenue net PLN',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_4'].emitter,
    };

    case_5_single_data_point: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.case_5_single_data_point',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.case_5_single_data_point',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                subtitle: 'Single data',
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            yColumnLabel: 'revenue_net_pln',
            metricName: 'Revenue net PLN',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_5'].emitter,
    };

    case_6_revenue_weekly: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.case_6_revenue_weekly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.case_6_revenue_weekly',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                subtitle:
                    'Data is aggregated weekly, so custom values should be a full date.',
            },
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            yColumnLabel: 'revenue_net_pln',
            metricName: 'Revenue net PLN',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_6'].emitter,
    };

    case_7_cloudsave_commits_count: IDashboardItemDataLoaderLink = {
        id: 'mopa.realtime_cloudsave_stats.chart.case_7_commits_cnt',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId:
                'mopa.realtime_cloudsave_stats.chart.case_7_commits_cnt',
            echartsSeriesType: 'bar',
            xColumnLabel: 'date',
            xAxisType: 'category',
            xAxisCategorySubtype: 'datetime',
            // xAxisCategoryDatetimeKey: 'date',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Commits count',
                subtitle:
                    'The X axis is a category type, so the data should be a full date.',
            },
            dashboardName: this.DASHBOARD_NAME,

            yColumnLabel: 'commits_cnt',
            metricName: 'Commits count',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_7'].emitter,
    };

    case_8: IDashboardItemDataLoaderLink = {
        id: 'mopa.realtime_cloudsave_stats.chart.case_8',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.realtime_cloudsave_stats.chart.case_8',
            dashboardItem: {
                title: 'Case 8 - splits and no data. Proper xAxis ordering',
                subtitle:
                    'Default echarts implementation does not order category axis properly when some dates are missing in data. Must be ordered using xAxis.data',
            },
            echartsSeriesType: 'bar',
            xColumnLabel: 'datetime',
            xAxisType: 'category',
            xAxisCategorySubtype: 'datetime',
            // xAxisCategoryDatetimeKey: 'date',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },

            yColumnLabel: 'cnt_all',
            metricName: 'cnt_all',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                            label,
                            context
                        );
                },
            },
        }),
        dataLoaderEmitter: this.chartDataLoaders['dev.echarts.case_8'].emitter,
    };

    case_10_quarterly: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.case_10_quarterly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'mopa.sales.chart.case_10_quarterly',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from splitColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Revenue',
                subtitle:
                    'Quarterly data – ticks show YEAR-MONTH, tooltip shows full date with quarter in parentheses, e.g. "2024-01 (Q1)".',
            },
            dashboardName: this.DASHBOARD_NAME,
            echartsSeriesType: 'bar',
            xColumnLabel: 'date',
            yColumnLabel: 'revenue_net_pln',
            metricName: 'Revenue net PLN',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['dev.echarts.case_10_quarterly'].emitter,
    };

    case_11_break_line_on_missing_data: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.case_11_break_line_on_missing_data',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId:
                'mopa.sales.chart.case_11_break_line_on_missing_data',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'Purchase complete count - break line on missing data - variant datetime',
                subtitle:
                    'When there is missing data in the middle of the chart, line should break to properly reflect that there is no data for that period.',
            },
            yColumnLabel: 'PurchaseComplete_count',
            metricName: 'Purchase complete count',
            dashboardName: this.DASHBOARD_NAME,
            xAxisType: 'category',
            xAxisCategorySubtype: 'datetime',
            xColumnLabel: 'datetime',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDatetimeTooltipFormatter(
                            label,
                            context
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'dev.echarts.case_11_break_line_on_missing_data'
            ].emitter,
    };

    case_11b_break_line_on_missing_data: IDashboardItemDataLoaderLink = {
        id: 'mopa.sales.chart.case_11b_break_line_on_missing_data',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId:
                'mopa.sales.chart.case_11b_break_line_on_missing_data',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            dashboardItem: {
                title: 'ARPDAU - break line on missing data - variant date + split',
                subtitle: 'Different case of previous chart',
            },
            yColumnLabel: 'wavg_ARPDAU_net_pln',
            metricName: 'ARPDAU',
            dashboardName: this.DASHBOARD_NAME,
            xColumnLabel: 'date',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (v: any) =>
                        EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                            v,
                            context.settings.currency || 'pln'
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'dev.echarts.case_11b_break_line_on_missing_data'
            ].emitter,
    };

    // Case 12 - session length distribution (UBI-1090). Both charts share one loader.
    // Point `xColumnLabel` below at the numeric `session_length` to reproduce the error:
    // the guard in XYChartAxis.applyEchartsCategoryXAxis throws instead of possibly drawing bars with huge gaps.
    case_12_bucket_label_category: IDashboardItemDataLoaderLink = {
        id: 'lnk.dev.echarts.chart.case_12_bucket_label_category',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'dev.echarts.chart.case_12_bucket_label_category',
            dashboardItem: {
                title: 'Session length distribution as string labels on a category axis',
                subtitle:
                    'In that case, we need to cast the sql column used as the X axis to a string type.',
            },
            xAxisType: 'category',
            xAxisCategorySubtype: 'generic',
            xColumnLabel: 'session_length_bucket',
            yColumnLabel: 'd3',
            metricName: 'Sessions',
            echartsSeriesType: 'bar',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter: () => (label: any) =>
                    `Duration: ${label} mins`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['dev.echarts.case_12_numeric_category_axis']
                .emitter,
    };

    case_12b_session_length_value: IDashboardItemDataLoaderLink = {
        id: 'lnk.dev.echarts.chart.case_12b_session_length_value',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'dev.echarts.chart.case_12b_session_length_value',
            dashboardItem: {
                title: 'Same bins as numbers on a value axis',
                subtitle:
                    'In that case, we need to use a line chart. A bar chart will extend slightly beyond the chart due to the center alignment.',
            },
            xAxisType: 'value',
            xColumnLabel: 'session_length',
            yColumnLabel: 'd3',
            metricName: 'Sessions',
            echartsSeriesType: 'line',
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter: () => (label: any) =>
                    `Duration from: ${label} mins`,
                valueFormatterGetter: () =>
                    EChartsTooltipHelper.tooltipIntegerValueFormatter,
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['dev.echarts.case_12_numeric_category_axis']
                .emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {};

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'DevEChartsChomponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'DevEChartsChomponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'Google Play',
                            'Microsoft Store',
                            'iTunes',
                            'iTunes macOS',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 365 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
        ];

        return initialContext;
    }
}
